/**
 * StreakSnapshotWidget — a compact, always-visible streak-v3 snapshot
 * card for the batch dashboard. Sits alongside the KPI strip on
 * StudentHome so students see their live streak state without
 * navigating to the dedicated Streak page.
 *
 * Complements — not replaces — the full Racing Track. This widget is
 * for at-a-glance status: streak count, tokens, grace, cleared/at-risk
 * badge, and a CTA to open the full racing view.
 *
 * Silently hides itself when the student's batch has streak disabled
 * (STREAK_DISABLED) or they have no active enrollment (NO_STREAK_BATCH).
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  Flame, Coins, ShieldCheck, CircleCheck, AlertTriangle, ArrowUpRight,
  Snowflake, Trophy, Clock,
} from "lucide-react";
import { API } from "../contexts/AuthContext";
import { fmt12h } from "../utils/timeFormat";

/** Same 4-tier taxonomy used by the Streak leaderboard, so the same
 *  student sees consistent chip colours across the app. */
/* Iter 192.4 — thematic tier names ("fire progression": Spark →
   Ember → Blaze → Inferno) with brand-appropriate colours. Each
   tier includes the LOWER bound so we can compute how far a student
   is from the next rank and surface it in the widget. */
const TIERS = [
  { min: 30, label: "Inferno", color: "#a855f7", bg: "rgba(168,85,247,0.14)", next: null },
  { min: 14, label: "Blaze",   color: "#f97316", bg: "rgba(249,115,22,0.14)", next: 30 },
  { min: 7,  label: "Ember",   color: "#0ea5e9", bg: "rgba(14,165,233,0.14)", next: 14 },
  { min: 0,  label: "Spark",   color: "#fbbf24", bg: "rgba(251,191,36,0.14)", next: 7 },
];
function tierFor(days) {
  return TIERS.find((t) => Number(days || 0) >= t.min) || TIERS[TIERS.length - 1];
}
function nextTierFor(days) {
  const d = Number(days || 0);
  if (d < 7)   return { label: "Ember",   at: 7  };
  if (d < 14)  return { label: "Blaze",   at: 14 };
  if (d < 30)  return { label: "Inferno", at: 30 };
  return null;
}

/** Iter 197.2 — refresh chip now honours the student's WINDOW opening.
    Previously hardcoded "12:00 AM (PKT)" which contradicted the new
    window-anchored streak-day boundary. */
function refreshLabelForWindow(window) {
  if (!window || !window.start_time) return "Streak day rolls at your check-in time";
  return `Streak day rolls at ${fmt12h(window.start_time)}`;
}

export default function StreakSnapshotWidget() {
  const [data, setData] = useState(null);
  const [hidden, setHidden] = useState(false);

  const refresh = useCallback(() => {
    axios.get(`${API}/me/streak-v3/today`)
      .then((r) => setData(r.data))
      .catch((e) => {
        const code = e?.response?.data?.detail?.code;
        // Hide silently for these known no-op states.
        if (code === "NO_FREE_BATCH" || code === "NO_STREAK_BATCH" || code === "STREAK_DISABLED") {
          setHidden(true);
        }
      });
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  if (hidden) return null;
  if (!data) {
    return (
      <div
        className="rounded-[16px] p-4 mb-6"
        style={{ background: "var(--px-surface)", border: "1px solid var(--px-line)" }}
      >
        <div className="portal-shimmer h-4 w-24 mb-2" />
        <div className="portal-shimmer h-6 w-40" />
      </div>
    );
  }

  const state = data.state || {};
  const day = data.day || {};
  const cfg = data.config || {};
  const v2 = data.v2 || {};
  const streak = Number(state.current_streak || 0);
  const best = Number(state.best_streak || 0);
  const tokens = Number(state.tokens_available || 0);
  const graceRemaining = Number(state.grace_remaining || 0);
  const graceCap = Number(cfg.grace_allowed_per_cycle || 0);
  const graceUsed = Math.max(0, graceCap - graceRemaining);
  const isFrozen = !!state.is_frozen_by_streak;
  const cleared = !!day.cleared;
  const progress = Number(day.progress_percent || 0);
  const tier = tierFor(streak);
  const next = nextTierFor(streak);
  const daysToNext = next ? Math.max(1, next.at - streak) : 0;
  // Progress bar within current tier — 0..1
  const tierProgress = next ? Math.min(1, streak / next.at) : 1;

  let statusText;
  let statusColor = tier.color;
  if (isFrozen) {
    statusText = "Account frozen";
    statusColor = "#93c5fd";
  } else if (cleared) {
    statusText = "Today cleared — see you tomorrow";
    statusColor = "#16a34a";
  } else if (!v2.window) {
    statusText = "Set your daily check-in window";
    statusColor = "#f59e0b";
  } else if (!day.checkin_at && !v2.in_window) {
    statusText = "Check-in window hasn't opened yet";
    statusColor = "#64748b";
  } else if (!day.checkin_at && v2.in_window) {
    statusText = "Check in — window is open now";
    statusColor = "#f59e0b";
  } else {
    const done = Number(day.tasks_done || 0);
    const total = Number(day.tasks_assigned || 0);
    statusText = `Tasks ${done}/${total} · MCQs ${day.mcqs_done || 0}/${cfg.daily_mcq_target || 0}`;
  }

  return (
    <div
      className="rounded-[18px] p-4 md:p-5 mb-6 relative overflow-hidden"
      style={{
        background: `linear-gradient(135deg, ${tier.bg} 0%, var(--px-surface) 65%)`,
        border: `1px solid ${tier.color}55`,
      }}
      data-testid="streak-snapshot-widget"
    >
      {/* Ambient glow */}
      <div
        aria-hidden
        className="absolute -top-14 -right-14 w-40 h-40 rounded-full pointer-events-none"
        style={{ background: `radial-gradient(circle, ${tier.color}22 0%, transparent 70%)` }}
      />

      <div className="flex items-start justify-between gap-3 mb-3 relative">
        <div className="flex items-center gap-2.5 min-w-0">
          <div
            className="w-11 h-11 rounded-full flex items-center justify-center shrink-0"
            style={{ background: `${tier.color}22`, border: `1.5px solid ${tier.color}55` }}
          >
            <Flame size={20} style={{ color: tier.color }} strokeWidth={2.5} />
          </div>
          <div className="min-w-0">
            <div className="text-[10.5px] font-bold uppercase tracking-[0.14em]" style={{ color: tier.color }}>
              {tier.label} {next ? `· ${daysToNext}d to ${next.label}` : "· Untouchable"}
            </div>
            <div className="flex items-baseline gap-1.5">
              <span className="text-[28px] font-black tabular-nums leading-none" style={{ color: tier.color }}>{streak}</span>
              <span className="text-[12.5px] font-semibold" style={{ color: "var(--px-ink-mute)" }}>day streak</span>
            </div>
          </div>
        </div>

        {/* Status pill — Iter 192.4: darker green for TODAY CLEARED
            so it reads clearly on the pale-green bg. */}
        {isFrozen ? (
          <StatusChip
            icon={Snowflake} label="Frozen" color="#1d4ed8"
            bg="#dbeafe" border="rgba(29,78,216,0.55)"
            testid="snap-status-frozen"
          />
        ) : cleared ? (
          <StatusChip
            icon={CircleCheck} label="Today cleared" color="#ffffff"
            bg="#16a34a" border="#15803d"
            testid="snap-status-cleared"
          />
        ) : (
          <StatusChip
            icon={AlertTriangle} label={`${progress}% today`} color="#92400e"
            bg="#fde68a" border="rgba(146,64,14,0.55)"
            testid="snap-status-inflight"
          />
        )}
      </div>

      {/* Tier progression bar — Iter 192.4. Shows current tier
          progress toward the next rank so students understand where
          they stand and what they're chasing. Only shown for
          non-Inferno tiers (Inferno is max — celebrate instead). */}
      {next && !isFrozen && (
        <div className="mb-3 relative" data-testid="snap-tier-progress">
          <div
            className="h-1.5 rounded-full overflow-hidden"
            style={{ background: "rgba(148,163,184,0.20)" }}
          >
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{
                width: `${tierProgress * 100}%`,
                background: `linear-gradient(90deg, ${tier.color}, ${tier.color}dd)`,
              }}
            />
          </div>
          <div className="flex items-center justify-between mt-1 text-[10.5px] font-semibold">
            <span style={{ color: tier.color }}>{tier.label} {streak}d</span>
            <span style={{ color: "var(--px-ink-mute)" }}>{next.label} {next.at}d →</span>
          </div>
        </div>
      )}

      {/* Refresh-time chip — Iter 192.4. Sits near the top so students
          never wonder "when will my streak update?". Small, clock-icon,
          no clutter. */}
      <div
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10.5px] font-semibold mb-3"
        style={{
          background: "rgba(15, 30, 74, 0.06)",
          border: "1px solid rgba(15, 30, 74, 0.12)",
          color: "var(--px-ink-mute)",
        }}
        data-testid="snap-refresh-info"
        title="Your streak day rolls at your check-in time (not calendar midnight)"
      >
        <Clock size={11} strokeWidth={2.4} /> {refreshLabelForWindow(data?.v2?.window)}
      </div>

      <div
        className="rounded-[10px] px-3 py-2.5 mb-3 text-[12.5px] font-semibold leading-snug"
        style={{ background: "rgba(148,163,184,0.06)", color: statusColor, border: `1px solid ${statusColor}33` }}
        data-testid="snap-status-line"
      >
        {statusText}
      </div>

      {/* Metric strip */}
      <div className="flex items-center gap-2 flex-wrap text-[12px] mb-3.5 relative">
        <Metric icon={Trophy}      label="best"   value={`${best}d`}   accent="#f59e0b" />
        <Metric icon={Coins}       label="tokens" value={tokens}       accent="#ca8a04" />
        {graceCap > 0 && (
          // iter 200 · show USED/CAP (not remaining) so the student
          // instantly understands "1/1 grace" = "you've spent your
          // safety net", per the founder's feedback.
          <Metric icon={ShieldCheck} label="grace used"  value={`${graceUsed}/${graceCap}`} accent={graceUsed >= graceCap ? "#b91c1c" : "#0284c7"} />
        )}
      </div>

      {/* Single CTA — always deep-links to the full page. */}
      <Link
        to="/student/streak"
        className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-[12.5px] font-bold transition"
        style={{ background: tier.color, color: "#0b1220" }}
        data-testid="snap-open-full"
      >
        {cleared ? "Open Streak page" : isFrozen ? "See details" : "Open Streak page"}
        <ArrowUpRight size={13} />
      </Link>
    </div>
  );
}

/* ─── Sub-components ──────────────────────────────────────────── */
function Metric({ icon: Icon, label, value, accent }) {
  return (
    <span
      className="inline-flex items-center gap-1 py-0.5 px-2 rounded-full"
      style={{ background: "rgba(148,163,184,0.10)" }}
    >
      <Icon size={11} style={{ color: accent }} />
      <span className="tabular-nums font-bold" style={{ color: "var(--px-ink)" }}>{value}</span>
      <span className="text-[10.5px]" style={{ color: "var(--px-ink-mute)" }}>{label}</span>
    </span>
  );
}

function StatusChip({ icon: Icon, label, color, bg, border, testid }) {
  return (
    <span
      className="inline-flex items-center gap-1 py-1 px-2 rounded-full text-[10.5px] font-bold shrink-0"
      style={{ background: bg, color, border: `1px solid ${border}` }}
      data-testid={testid}
    >
      <Icon size={11} /> {label}
    </span>
  );
}
